import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Badge } from '@shared/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@shared/components/ui/dialog'
import { Checkbox } from '@shared/components/ui/checkbox'
import { toast } from '@shared/hooks/use-toast'
import { apiUrl } from '@/lib/api'

interface UserRow {
	id: string
	email: string
	display_name: string
	phone?: string
	is_active: boolean
	created_at: string
	roles: string[]
}

interface RoleRow {
	id: string
	name: string
	description?: string
	user_count: number
}

interface InviteRow {
	id: string
	email: string
	role_ids: string[]
	token: string
	expires_at: string
	redeemed_at?: string
	created_at: string
}

interface TenantInfo {
	id: string
	name: string
}

const UserManagementPage: React.FC = () => {
	const { tenantId } = useParams<{ tenantId: string }>()
	const [tenant, setTenant] = useState<TenantInfo | null>(null)
	const [users, setUsers] = useState<UserRow[]>([])
	const [roles, setRoles] = useState<RoleRow[]>([])
	const [invites, setInvites] = useState<InviteRow[]>([])
	const [loading, setLoading] = useState(true)
	const [createOpen, setCreateOpen] = useState(false)
	const [editOpen, setEditOpen] = useState(false)
	const [resetOpen, setResetOpen] = useState(false)
	const [selectedUser, setSelectedUser] = useState<UserRow | null>(null)
	const [newPassword, setNewPassword] = useState('')
	const [inviteMode, setInviteMode] = useState(false)


	const [form, setForm] = useState({
		email: '',
		password: '',
		display_name: '',
		phone: '',
		roleIds: [] as string[],
	})

	const load = async () => {
		if (!tenantId) return
		setLoading(true)
		try {
			const [tRes, uRes, rRes, iRes] = await Promise.all([
				fetch(apiUrl(`/api/admin/tenants/${tenantId}`), { headers: await (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })() }),
				fetch(apiUrl(`/api/admin/tenants/${tenantId}/users`), { headers: await (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })() }),
				fetch(apiUrl(`/api/admin/tenants/${tenantId}/roles`), { headers: await (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })() }),
				fetch(apiUrl(`/api/admin/tenants/${tenantId}/invites`), { headers: await (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })() }),
			])
			if (!tRes.ok || !uRes.ok || !rRes.ok || !iRes.ok) throw new Error('Failed to load data')
			const tJson = await tRes.json()
			const uJson = await uRes.json()
			const rJson = await rRes.json()
			const iJson = await iRes.json()
			setTenant({ id: tJson.id, name: tJson.name })
			setUsers(uJson.users || [])
			setRoles(rJson.roles || [])
			setInvites(iJson.invites || [])
		} catch (e) {
			toast({ title: 'Error', description: 'Failed to load users/roles', variant: 'destructive' })
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => { load() }, [tenantId])

	const handleCreate = async () => {
		if (!tenantId) return
		if (!inviteMode && !isFirstUser && form.roleIds.length === 0) {
			toast({ title: 'Select a role', description: 'Please select at least one role for this user', variant: 'destructive' })
			return
		}
		try {
			if (inviteMode) {
				const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/invites`), {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', ...await (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })() },
					body: JSON.stringify({ email: form.email, roleIds: form.roleIds })
				})
				if (!res.ok) throw new Error('Failed to create invite')
				toast({ title: 'Invite created' })
				setCreateOpen(false)
				setForm({ email: '', password: '', display_name: '', phone: '', roleIds: [] })
				load()
			} else {
				const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/users`), {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', ...await (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })() },
					body: JSON.stringify(form),
				})
				if (!res.ok) {
					const err = await res.json().catch(() => ({}))
					throw new Error(err.error || 'Failed to create user')
				}
				toast({ title: 'User created' })
				setCreateOpen(false)
				setForm({ email: '', password: '', display_name: '', phone: '', roleIds: [] })
				load()
			}
		} catch (e: any) {
			toast({ title: 'Error', description: e.message || 'Failed', variant: 'destructive' })
		}
	}

	const handleUpdate = async () => {
		if (!tenantId || !selectedUser) return
		try {
			const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/users/${selectedUser.id}`), {
				method: 'PUT',
				headers: { 'Content-Type': 'application/json', ...await (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })() },
				body: JSON.stringify({
					display_name: form.display_name,
					phone: form.phone,
					roleIds: form.roleIds,
				}),
			})
			if (!res.ok) {
				const err = await res.json().catch(() => ({}))
				throw new Error(err.error || 'Failed to update user')
			}
			toast({ title: 'User updated' })
			setEditOpen(false)
			setSelectedUser(null)
			load()
		} catch (e: any) {
			toast({ title: 'Error', description: e.message || 'Failed', variant: 'destructive' })
		}
	}

	const handleDelete = async (userId: string) => {
		if (!tenantId) return
		if (!confirm('Delete this user?')) return
		try {
			const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/users/${userId}`), {
				method: 'DELETE', headers: await (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })(),
			})
			if (!res.ok) {
				const err = await res.json().catch(() => ({}))
				throw new Error(err.error || 'Failed to delete user')
			}
			toast({ title: 'User deleted' })
			load()
		} catch (e: any) {
			toast({ title: 'Error', description: e.message || 'Failed', variant: 'destructive' })
		}
	}

	const handleResetPassword = async () => {
		if (!tenantId || !selectedUser) return
		try {
			const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/users/${selectedUser.id}/reset-password`), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', ...await (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })() },
				body: JSON.stringify({ newPassword }),
			})
			if (!res.ok) {
				const err = await res.json().catch(() => ({}))
				throw new Error(err.error || 'Failed to reset password')
			}
			toast({ title: 'Password reset' })
			setResetOpen(false)
			setNewPassword('')
		} catch (e: any) {
			toast({ title: 'Error', description: e.message || 'Failed', variant: 'destructive' })
		}
	}

	const resendInvite = async (inviteId: string) => {
		if (!tenantId) return
		const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/invites/${inviteId}/resend`), { method: 'POST', headers: await (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })() })
		if (!res.ok) { toast({ title: 'Error', description: 'Failed to resend invite', variant: 'destructive' }); return }
		toast({ title: 'Invite resent' })
	}
	const revokeInvite = async (inviteId: string) => {
		if (!tenantId) return
		const res = await fetch(apiUrl(`/api/admin/tenants/${tenantId}/invites/${inviteId}`), { method: 'DELETE', headers: await (async () => { const { auth } = await import('@/lib/firebase'); const t = await auth.currentUser?.getIdToken(); return t ? { 'Authorization': `Bearer ${t}` } : {} })() })
		if (!res.ok) { toast({ title: 'Error', description: 'Failed to revoke invite', variant: 'destructive' }); return }
		toast({ title: 'Invite revoked' })
		load()
	}

	const isFirstUser = users.length === 0;

	if (loading) return <div className="p-6">Loading...</div>

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold">Users</h1>
					{tenant && <div className="text-sm text-muted-foreground">Tenant: {tenant.name}</div>}
				</div>
				<div className="space-x-2 flex items-center">
					<Link to={`/tenants/${tenantId}`}><Button variant="outline">Back to Tenant</Button></Link>
					<Dialog open={createOpen} onOpenChange={setCreateOpen}>
						<DialogTrigger asChild>
							<Button>Create User</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Create User</DialogTitle>
							</DialogHeader>
							<div className="space-y-4">
								<div className="flex items-center gap-2">
									<Checkbox checked={inviteMode} onCheckedChange={v => setInviteMode(Boolean(v))} />
									<span>Invite user (send email) instead of setting a password now</span>
								</div>
								<div>
									<Label>Email</Label>
									<Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
								</div>
								{!inviteMode && (
									<div>
										<Label>Password</Label>
										<Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
									</div>
								)}
								<div>
									<Label>Display Name</Label>
									<Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
								</div>
								<div>
									<Label>Phone</Label>
									<Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
								</div>
								<div>
									<Label>Roles</Label>
									<div className="grid grid-cols-2 gap-2 mt-2">
										{roles.map((r) => (
											<label key={r.id} className="flex items-center space-x-2">
												<Checkbox checked={form.roleIds.includes(r.id)} onCheckedChange={(v) => {
													const checked = Boolean(v)
													setForm((prev) => ({
														...prev,
														roleIds: checked ? [...prev.roleIds, r.id] : prev.roleIds.filter((id) => id !== r.id),
													}))
												}} />
												<span>{r.name}</span>
											</label>
										))}
									</div>
									{!isFirstUser && form.roleIds.length === 0 && (
										<p className="text-xs text-red-500 mt-1">Select at least one role.</p>
									)}
									{isFirstUser && form.roleIds.length === 0 && (
										<p className="text-xs text-muted-foreground mt-1">No roles selected: Admin will be auto-assigned for the first tenant user.</p>
									)}
								</div>
								<Button onClick={handleCreate} className="w-full">{inviteMode ? 'Send Invite' : 'Create'}</Button>
							</div>
						</DialogContent>
					</Dialog>
				</div>
			</div>

			<div className="grid gap-4">
				{users.map((u) => (
					<Card key={u.id}>
						<CardHeader>
							<CardTitle className="flex items-center justify-between">
								<span>{u.display_name} <span className="text-sm text-muted-foreground font-normal">({u.email})</span></span>
								<span>
									{u.roles.map((r) => (
										<Badge key={r} variant="outline" className="ml-1">{r}</Badge>
									))}
								</span>
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="flex items-center space-x-2">
								<Button variant="outline" onClick={() => {
									setSelectedUser(u)
									setForm({ email: u.email, password: '', display_name: u.display_name, phone: u.phone || '', roleIds: u.roles.map((n) => roles.find((r) => r.name === n)?.id!).filter(Boolean) })
									setEditOpen(true)
								}}>Edit</Button>
								<Button variant="outline" onClick={() => { setSelectedUser(u); setResetOpen(true) }}>Reset Password</Button>
								<Button variant="destructive" onClick={() => handleDelete(u.id)}>Delete</Button>
							</div>
						</CardContent>
					</Card>
				))}
			</div>

			{/* Pending invites */}
			{invites.length > 0 && (
				<Card>
					<CardHeader><CardTitle>Pending Invites</CardTitle></CardHeader>
					<CardContent>
						<div className="space-y-2">
							{invites.map((i) => (
								<div key={i.id} className="flex items-center justify-between border border-border rounded p-2">
									<div className="text-sm">
										<div>{i.email}</div>
										<div className="text-xs text-muted-foreground">Expires: {new Date(i.expires_at).toLocaleString()} {i.redeemed_at ? `· Redeemed ${new Date(i.redeemed_at).toLocaleString()}` : ''}</div>
									</div>
									<div className="space-x-2">
										<Button variant="outline" size="sm" onClick={() => resendInvite(i.id)}>Resend</Button>
										<Button variant="destructive" size="sm" onClick={() => revokeInvite(i.id)}>Revoke</Button>
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Edit dialog */}
			<Dialog open={editOpen} onOpenChange={setEditOpen}>
				<DialogContent>
					<DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
					<div className="space-y-4">
						<div>
							<Label>Display Name</Label>
							<Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
						</div>
						<div>
							<Label>Phone</Label>
							<Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
						</div>
						<div>
							<Label>Roles</Label>
							<div className="grid grid-cols-2 gap-2 mt-2">
								{roles.map((r) => (
									<label key={r.id} className="flex items-center space-x-2">
										<Checkbox checked={form.roleIds.includes(r.id)} onCheckedChange={(v) => {
											const checked = Boolean(v)
											setForm((prev) => ({
												...prev,
												roleIds: checked ? [...prev.roleIds, r.id] : prev.roleIds.filter((id) => id !== r.id),
											}))
										}} />
										<span>{r.name}</span>
									</label>
								))}
							</div>
						</div>
						<Button onClick={handleUpdate} className="w-full">Save</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Reset password */}
			<Dialog open={resetOpen} onOpenChange={setResetOpen}>
				<DialogContent>
					<DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
					<div className="space-y-4">
						<Label>New Password</Label>
						<Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
						<Button onClick={handleResetPassword} className="w-full">Reset</Button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default UserManagementPage


